import React, { useState, useEffect, useReducer, Fragment } from "react";
import { Link } from "react-router-dom";
import {
  Container,
  Image,
  Card,
  Badge,
  Button,
  Dropdown,
  ListGroup,
  Alert,
  Modal,
  Spinner,
} from "react-bootstrap";
import key from "weak-key";

import DataTable from "../../components/table";
import Receipt from "../donation/receipt";
import { transformProfilePicture } from "../../components/transformFileURL";

import { getData, postData } from "../../services/apiService";
import { useAuthContext } from "../../context/authProvider";
import { decodeToken } from "../../utils/session";
import { notify, initialState } from "../../store/notification";
import { OrgProfileURL, NeedProfileURL } from "../../constants";

/**
 * Component for managing giving history.
 * This component provides functionality for users to view their contributions to needs.
 * @returns {React.Element} - Returns JSX for managing giving history.
 * @access Accessible by donor
 */
const ManageGivingHistory = () => {
  // Notification state and dispatch hook
  const [notification, dispatch] = useReducer(notify, initialState);
  // Destructure the 'userSession' object from the 'useAuthContext()' hook
  const { userSession } = useAuthContext();
  // Decode the user session token
  const user = decodeToken(userSession);

  const [isLoading, setIsLoading] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [givingHistories, setGivingHistories] = useState([]);
  const [transaction, setTransaction] = useState({});
  const [receipt, setReceipt] = useState({});
  const [page, setPage] = useState({ PageNumber: 1, PageSize: 25 });
  const [searchTerm, setSearchTerm] = useState("");
  const [totalRecords, setTotalRecords] = useState(0);

  // State and functions for controlling the Allocation Modal
  const [displayAllocationModal, setDisplayAllocationModal] = useState(false);
  const closeAllocationModal = () => setDisplayAllocationModal(false);
  const openAllocationModal = () => setDisplayAllocationModal(true);

  // State and functions for controlling the Receipt Modal
  const [displayReceiptModal, setDisplayReceiptModal] = useState(false);
  const closeReceiptModal = () => setDisplayReceiptModal(false);
  const openReceiptModal = () => setDisplayReceiptModal(true);

  // State to control whether the transaction receipt  should be opened for printing
  const [hasToPrint, setHasToPrint] = useState(false);

  // Columns configuration for giving history table
  const columns = [
    {
      header: "Date",
      accessorKey: "paidOn",
    },
    // {
    //   header: "Order Id",
    //   accessorKey: "transactionId",
    // },
    // {
    //   header: "Origin",
    //   accessorKey: "origin",
    // },
    // {
    //   header: "Source",
    //   accessorKey: "source",
    // },
    // {
    //   header: "Need Id",
    //   accessorKey: "needId",
    // },
    // {
    //   header: "Donor",
    //   accessorKey: "donor",
    // },
    {
      header: "Recipient",
      cell: (cellProps) => {
        return (
          <Fragment>
            <Link
              to={`${NeedProfileURL}${cellProps.row.original.needReferenceId}/${cellProps.row.original.needId}`}
              className="text-decoration-none"
            >
              {cellProps.row.original.needTitle}
            </Link>{" "}
            {cellProps.row.original.needId > 0 ? "for " : "Additional Gift"}
            <span className="fw-semibold">
              {cellProps.row.original.recipient}
            </span>
          </Fragment>
        );
      },
    },
    {
      header: "Organization",
      cell: (cellProps) => {
        return (
          <Link
            to={`${OrgProfileURL}${cellProps.row.original.organizationReferenceId}`}
            className="text-decoration-none"
          >
            {cellProps.row.original.organizationName}
          </Link>
        );
      },
      accessorKey: "organizationName",
    },
    {
      header: "Donation Amount",
      cell: (cellProps) => {
        return <Fragment> ${cellProps.row.original.donationAmount}</Fragment>;
      },
    },
    // {
    //   header: "Additional Gift",
    //   cell: (cellProps) => {
    //     return <Fragment> ${cellProps.row.original.contribution}</Fragment>;
    //   },
    // },
    // {
    //   header: "Processing Fee",
    //   cell: (cellProps) => {
    //     return <Fragment> ${cellProps.row.original.processingFee}</Fragment>;
    //   },
    // },
    // {
    //   header: "Transaction Amount",
    //   cell: (cellProps) => {
    //     return (
    //       <Fragment> ${cellProps.row.original.transactionAmount}</Fragment>
    //     );
    //   },
    // },
    // {
    //   header: "Net Amount",
    //   cell: (cellProps) => {
    //     return <Fragment> ${cellProps.row.original.netAmount}</Fragment>;
    //   },
    // },
    // {
    //   header: "Tribute",
    //   cell: (cellProps) => {
    //     return (
    //       <Fragment>
    //         {cellProps.row.original.hasTribute && (
    //           <i className="flaticon-tick fs-3 d-inline-block text-center bg-secondary text-white rounded-circle hasTribute"></i>
    //         )}
    //       </Fragment>
    //     );
    //   },
    // },
    {
      header: "Status",
      cell: (cellProps) => {
        let background;
        switch (cellProps.row.original.status) {
          case "Initiated":
            background = "light";
            break;
          case "Success":
            background = "success";
            break;
          case "Processing":
            background = "info";
            break;
          case "Failed":
            background = "danger";
            break;
          case "Canceled":
            background = "warning";
            break;
          default:
            background = "dark";
            break;
        }
        return (
          <Badge className="fw-semibold text-dark" bg={background}>
            {cellProps.row.original.status.toUpperCase()}
          </Badge>
        );
      },
    },
    {
      header: "Actions",
      accessorKey: "id",
      cell: (cellProps) => {
        return (
          <Fragment>
            <Dropdown drop="down" className="more-menu">
              <Dropdown.Toggle variant="light" size="sm">
                <i className="flaticon-more"></i>
              </Dropdown.Toggle>

              <Dropdown.Menu>
                <Dropdown.Item
                  onClick={() =>
                    viewDonationDetails(
                      cellProps.row.original.referenceId,
                      cellProps.row.original.status
                    )
                  }
                >
                  <i className="flaticon-dollar-circle fs-5 me-2"></i>Donation
                  Details
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={() =>
                    viewReceipt(cellProps.row.original.referenceId)
                  }
                >
                  <i className="flaticon-invoice fs-5 me-2"></i>
                  Receipt
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </Fragment>
        );
      },
    },
  ];

  useEffect(() => {
    /**
     * Function to fetch giving history made by donor from the backend API
     * Executes when there is a change in the values of the dependencies in the useEffect hook.
     */
    const getGivingHistories = async () => {
      setIsLoading(true);
      try {
        const response = await postData(
          `/api/donor/giving-history`,
          {
            userId: user.nameid,
            page,
            searchTerm,
          },
          userSession
        );
        setGivingHistories(response.transactions);
        if (response.count != null) setTotalRecords(response.count);
        setIsLoading(false);
      } catch (error) {
        // Show error notification if an error occurs
        showNotification("danger", error, 5000);
        setGivingHistories([]);
        setIsLoading(false);
      }
    };

    if (user?.nameid) getGivingHistories();

    // Return a cleanup function
    return () => {};
  }, [searchTerm, page, userSession, user?.nameid]);

  /**
   * Function to handle page change event.
   * @param {number} pageNumber - The new page number.
   * @param {number} pageSize - The new page size.
   */
  const handlePageChange = (pageNumber, pageSize) => {
    setGivingHistories([]);
    setPage({
      PageNumber: pageNumber,
      PageSize: pageSize,
    });
  };

  /**
   * Function to handle search action.
   * @param {string} value - The search term entered by the user.
   */
  const handleSearch = (value) => {
    handlePageChange(1, page.PageSize);
    setSearchTerm(value);
  };

  /**
   * Function to fetch and view donation details.
   * @param {string} referenceId - The reference ID of the transaction.
   * @param {string} transactionStatus - The status of the transaction.
   */
  const viewDonationDetails = async (referenceId, transactionStatus) => {
    setIsPageLoading(true);
    setTransaction({});

    try {
      const response = await getData(
        `/api/payment/get-allocation-details/${referenceId}`,
        null,
        userSession
      );
      if (response) {
        response.allocations = transformProfilePicture(response.allocations);
        setTransaction(response);
        openAllocationModal();
      } else
        showNotification(
          "danger",
          "At the moment, there isn't any information available regarding donations for viewing.",
          5000
        );
      setIsPageLoading(false);
    } catch (error) {
      // Show error notification if an error occurs
      showNotification("danger", error, 5000);
      setTransaction({});
      setIsPageLoading(false);
    }
  };

  /**
   * Function to fetch and view receipt details.
   * @param {string} referenceId - The reference ID of the transaction.
   */
  const viewReceipt = async (referenceId) => {
    setIsPageLoading(true);
    setReceipt({});
    try {
      const response = await getData(
        `/api/payment/get-receipt/${referenceId}`,
        null,
        userSession
      );
      if (Object.keys(response).length > 0) {
        setReceipt(response);
        openReceiptModal();
      }
      setIsPageLoading(false);
    } catch (error) {
      showNotification("danger", error, 5000);
      setReceipt({});
      setIsPageLoading(false);
    }
  };

  /**
   * Function to bind a status to a corresponding badge with appropriate background color.
   * @param {string} status - The status to be bound to a badge.
   * @returns {JSX.Element} - A Badge component representing the status with appropriate background color.
   */
  const bindNeedStatus = (status) => {
    let background;
    switch (status) {
      case "Draft":
        background = "light";
        break;
      case "Awaiting Approval":
        background = "warning";
        break;
      case "Published":
        background = "info";
        break;
      case "Funded":
        background = "success";
        break;
      case "Expired":
      case "Closed":
        background = "danger";
        break;
      default:
        background = "dark";
        break;
    }
    return (
      <Badge className="fw-semibold text-dark" bg={background}>
        {status.toUpperCase()}
      </Badge>
    );
  };

  /**
   * Function to dispatch an action to show a notification.
   * @param {string} variant - The variant of the notification (e.g., 'success', 'error', 'info').
   * @param {string} message - The message content of the notification.
   * @param {number} timeout - The duration in milliseconds before the notification auto-dismisses (optional).
   */
  const showNotification = (variant, message, timeout) => {
    dispatch({
      type: "SHOW_NOTIFICATION",
      payload: {
        variant: variant,
        message: message,
        timeout: timeout,
      },
      dispatch: dispatch, // Pass dispatch function to the payload for updating notification state
    });
  };

  return (
    <main>
      <Container fluid>
        <h6 className="module-title fw-semibold mb-4">Giving History</h6>

        <Card className="border-0 rounded-3 transactions">
          <Card.Body className="p-4">
            {/* DataTable component for listing giving histories */}
            <DataTable
              columns={columns}
              data={givingHistories}
              totalRecords={totalRecords}
              page={page}
              onPageChange={handlePageChange}
              searchRecords={handleSearch}
              loadingState={isLoading}
            />
          </Card.Body>
        </Card>
      </Container>

      <Modal
        show={isPageLoading}
        aria-labelledby="Loading"
        className="modal-loading"
        centered
      >
        <Modal.Body className="text-center">
          <Spinner animation="border" role="status" variant="primary">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </Modal.Body>
      </Modal>
      <Modal
        show={displayAllocationModal}
        aria-labelledby="Donation Details Modal"
        className="modal-donation-details"
        dialogClassName="modal-feature"
        centered
      >
        <Modal.Body>
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="fw-semibold mb-0">Donation Details</h6>
            <Button
              className="btn-close fs-small"
              variant="link"
              aria-label="Close"
              onClick={closeAllocationModal}
            ></Button>
          </div>
          <hr />
          <ListGroup as="ul" className="mt-4">
            {/* <ListGroup.Item
              as="li"
              className="d-flex justify-content-between align-items-center bg-transparent px-3 py-2 fs-small"
            >
              <span>Reference ID:</span>
              <span>{transaction.referenceId}</span>
            </ListGroup.Item> */}
            <ListGroup.Item
              as="li"
              className="d-flex justify-content-between align-items-center bg-transparent px-3 py-2 fs-small"
            >
              <span>Total Donation Amount:</span>
              <span>${transaction.donationAmount}</span>
            </ListGroup.Item>
            <ListGroup.Item
              as="li"
              className="d-flex justify-content-between align-items-center bg-transparent px-3 py-2 fs-small"
            >
              <span>Additional Gift:</span>
              <span>${transaction.contribution}</span>
            </ListGroup.Item>
            {/* <ListGroup.Item
              as="li"
              className="d-flex justify-content-between align-items-center bg-transparent px-3 py-2 fs-small"
            >
              <span>Total Transaction Amount:</span>
              <span>${transaction.transactionAmount}</span>
            </ListGroup.Item> */}
            {/* <ListGroup.Item
              as="li"
              className="d-flex justify-content-between align-items-center bg-transparent px-3 py-2 fs-small"
            >
              <span>Processing Fee:</span>
              <span>${transaction.processingFee || 0}</span>
            </ListGroup.Item> */}
            <ListGroup.Item
              as="li"
              className="d-flex justify-content-between align-items-center bg-transparent px-3 py-2 fs-small"
            >
              <span>Total Net Amount:</span>
              <span>${transaction.netAmount}</span>
            </ListGroup.Item>
            <ListGroup.Item
              as="li"
              className="d-flex justify-content-between align-items-center bg-transparent px-3 py-2 fs-small"
            >
              <span>Source: </span>
              <span>{transaction.source}</span>
            </ListGroup.Item>
            {transaction.hasTribute && (
              <ListGroup.Item
                as="li"
                className="d-flex justify-content-between align-items-center bg-transparent px-3 py-2 fs-small"
              >
                <span>Tribute:</span>
                <span>
                  <i className="flaticon-tick fs-3 d-inline-block text-center bg-secondary text-white rounded-circle hasTribute"></i>
                </span>
              </ListGroup.Item>
            )}
            {transaction.allocations?.map((allocation) => (
              <ListGroup.Item
                as="li"
                key={key(allocation)}
                className="d-flex justify-content-between align-items-center bg-transparent p-3"
              >
                <div className="d-flex align-items-center">
                  <Image
                    src={allocation.profilePicture}
                    alt="Recipient"
                    roundedCircle
                    thumbnail
                    fluid
                    className="avatar"
                  />
                  <Link
                    to={`${NeedProfileURL}${allocation.referenceId}/${allocation.needId}`}
                    title={allocation.title}
                    className="text-decoration-none mx-3 text-dark fw-semibold"
                  >
                    {allocation.recipient}
                    <small className="d-block fw-light mt-1">
                      {allocation.title}
                    </small>
                    <small className="d-block fw-light mt-1">
                      [Donation Amount: <b>${allocation.donationAmount}</b> Net
                      Amount: <b>${allocation.netAmount}</b>]
                    </small>
                  </Link>
                </div>
                <div>{bindNeedStatus(allocation.needStatus)}</div>
              </ListGroup.Item>
            ))}
          </ListGroup>
        </Modal.Body>
      </Modal>
      <Modal
        show={displayReceiptModal}
        aria-labelledby="Receipt Modal"
        className="modal-receipt"
        dialogClassName="modal-feature"
        contentClassName="bg-transparent border-0"
        centered
      >
        <Modal.Body className="p-0">
          <Receipt
            receipt={receipt}
            hasToPrint={hasToPrint}
            setHasToPrint={setHasToPrint}
          />
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button
            variant="primary"
            onClick={closeReceiptModal}
            className="btn-md"
          >
            Close
          </Button>
          <Button
            variant="secondary"
            onClick={() => setHasToPrint(true)}
            className="mx-3 btn-md"
          >
            Print Receipt
          </Button>
        </Modal.Footer>
      </Modal>
      {/* Notification alerts */}
      <div className={`notification ${notification.variant && "show"}`}>
        {notification.variant && (
          <Alert
            variant={notification.variant}
            onClose={() => dispatch({ type: "CLEAR_NOTIFICATION" })}
            dismissible
          >
            {notification.message}
          </Alert>
        )}
      </div>
    </main>
  );
};

export default ManageGivingHistory;
